import React, { useMemo, useState } from "react";
import {
  Box, Paper, Typography, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Checkbox, IconButton, TextField, TablePagination,
  Chip, Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";

const dividerSx = (theme) => `1px solid ${theme.palette.divider}`;

// Alternado gris / sin color para diferenciar visualmente placas con varios modems (AVL) —
// se asigna por orden de aparición en `rows`, así que la misma placa siempre cae en la
// misma franja mientras esté visible en la página actual.
const PLACA_COLORS = ["#9e9e9e", null];

const TIPO_SERIE_LABELS = [
  ["radio",   "Radios",    "info"],
  ["bateria", "Baterías",  "secondary"],
  ["varios",  "Equipos",   "secondary"],
  ["chip",    "Chips",     "warning"],
];

export default function HistorialResultsTable({
  title = "Resultados",
  subtitle = "",
  loading = false,
  error = null,
  columns = [],
  rows = [],
  tipo = "",
  onClear,
  onEdit,
  onSelectionChange,
  pagination = null,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100, 200],
  searchEnabled = false,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Buscar...",
  checkboxEnabled = true,
  editEnabled = true,
  getRowSx = null,
  isCellClickable = null,
  onCellClick = null,
}) {
  const [selected, setSelected] = useState([]);

  // ✅ Ya no filtramos placa — el padre decide qué columnas mostrar
  // Identificador único de la fila para la selección. La placa no sirve (varias radios/equipos tienen "0" o vacía y se
  // marcaban juntos); radios, baterías y varios se identifican por su serie. Si no hay ninguno, el índice.
  const getRowId = (row, index) =>
    row?.id || row?.num_sim || row?.emai || row?.serie || row?.id2 || row?.numero || `fila-${index}`;

  // Resumen de cantidades (placas/modems/chips) — solo se muestra si la tabla
  // tiene esas columnas; se calcula sobre las filas cargadas (pagina actual).
  const summaryChips = useMemo(() => {
    const hasNumSim = columns.some((c) => c.key === "num_sim");
    const hasChips  = columns.some((c) => c.key === "chips");
    const hasSerie  = columns.some((c) => c.key === "serie");

    // Para AVL/Modem: solo el propio num_sim identifica el equipo.
    // Para los demas tipos (radio/bateria/varios/chip) se cuenta por "serie",
    // usando la etiqueta que corresponde segun el tipo de vista.
    const tipoMatch = !hasNumSim && hasSerie
      ? TIPO_SERIE_LABELS.find(([kw]) => tipo.includes(kw))
      : null;

    // La cantidad de placas solo aplica a AVL — en TKR y Varios no se muestra.
    const hasPlaca = columns.some((c) => c.key === "placa") && !tipoMatch;

    if (!hasPlaca && !hasNumSim && !hasChips && !tipoMatch) return [];

    const placas = new Set();
    const modems = new Set();
    const chips  = new Set();
    const series = new Set();
    for (const row of rows) {
      if (hasPlaca && row?.placa) placas.add(row.placa);
      if (hasNumSim && row?.num_sim) modems.add(row.num_sim);
      if (hasChips && Array.isArray(row?.chips)) {
        for (const c of row.chips) {
          if (c?.numero) chips.add(c.numero);
        }
      }
      if (tipoMatch && row?.serie) series.add(row.serie);
    }

    // Si el backend manda el total real (todas las paginas, no solo la
    // cargada) en pagination.totalPlacas/totalModems/totalChips, se usa ese —
    // sino se cae al conteo de la pagina actual con el aviso "(pág. actual)",
    // como antes. Es opt-in por pantalla: no todos los endpoints lo mandan todavia.
    const suffix = pagination && pagination.total > rows.length ? " (pág. actual)" : "";
    const placasCount = pagination?.totalPlacas ?? placas.size;
    const placasSuffix = pagination?.totalPlacas != null ? "" : suffix;
    const modemsCount = pagination?.totalModems ?? modems.size;
    const modemsSuffix = pagination?.totalModems != null ? "" : suffix;
    const chipsCount = pagination?.totalChips ?? chips.size;
    const chipsSuffix = pagination?.totalChips != null ? "" : suffix;

    const result = [];
    if (hasPlaca) result.push({ label: `Placas: ${placasCount}${placasSuffix}`, color: "primary" });
    if (hasNumSim) result.push({ label: `Modems: ${modemsCount}${modemsSuffix}`, color: "success" });
    if (hasChips) result.push({ label: `Chips: ${chipsCount}${chipsSuffix}`, color: "warning" });
    if (tipoMatch) {
      const [, equipoLabel, equipoColor] = tipoMatch;
      result.push({ label: `${equipoLabel}: ${series.size}${suffix}`, color: equipoColor });
    }
    return result;
  }, [rows, columns, pagination, tipo]);

  const placaColorMap = useMemo(() => {
    const map = new Map();
    let i = 0;
    for (const row of rows) {
      const p = row?.placa;
      if (p && !map.has(p)) {
        map.set(p, PLACA_COLORS[i % PLACA_COLORS.length]);
        i += 1;
      }
    }
    return map;
  }, [rows]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n, i) => getRowId(n, i));
      setSelected(newSelecteds);
      onSelectionChange?.(newSelecteds);
      return;
    }
    setSelected([]);
    onSelectionChange?.([]);
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    const newSelected =
      selectedIndex === -1 ? [...selected, id] : selected.filter((x) => x !== id);
    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  };

  const pageZeroBased = Math.max((pagination?.page ?? 1) - 1, 0);
  const limit = pagination?.limit ?? 50;
  const total = pagination?.total ?? rows.length;

  return (
    <Paper elevation={0} sx={{ mt: 1, border: dividerSx, borderRadius: 1, overflow: "hidden" }}>
      {(title || subtitle || searchEnabled) && (
        <Box
          sx={{
            px: 1.5, py: 0.8, display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 1,
            bgcolor: (theme) => theme.palette.action.hover, borderBottom: dividerSx,
          }}
        >
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary"
                sx={{ display: "block", fontSize: "0.65rem", mt: -0.5 }}>
                {subtitle}
              </Typography>
            )}
            {summaryChips.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {summaryChips.map((s) => (
                  <Chip
                    key={s.label}
                    size="small"
                    color={s.color}
                    variant="outlined"
                    label={s.label}
                    sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
            {searchEnabled && (
              <TextField
                size="small"
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder={searchPlaceholder}
                sx={{ width: 230, "& .MuiInputBase-root": { height: 32, fontSize: "0.8rem" } }}
              />
            )}
            {onClear && (
              <Button size="small" onClick={onClear}
                sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0 }}>
                Ocultar
              </Button>
            )}
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 1 }}>
          <Alert severity="error" sx={{ py: 0, fontSize: "0.75rem" }}>{error}</Alert>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" padding="none">
              <TableHead sx={{ bgcolor: (theme) => theme.palette.action.hover }}>
                <TableRow>
                  {checkboxEnabled && (
                    <TableCell sx={{ width: 36, pl: 1 }}>
                      <Checkbox
                        size="small"
                        indeterminate={selected.length > 0 && selected.length < rows.length}
                        checked={rows.length > 0 && selected.length === rows.length}
                        onChange={handleSelectAllClick}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                  )}

                  {/* ✅ Todas las columnas vienen del padre, incluyendo placa si aplica */}
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{ py: 0.5, fontWeight: 700, fontSize: "0.7rem", color: "text.secondary", ...col.headerSx }}
                    >
                      {String(col.header || "").toUpperCase()}
                    </TableCell>
                  ))}

                  {editEnabled && (
                    <TableCell align="right"
                      sx={{ py: 0.5, pr: 1.5, fontWeight: 700, fontSize: "0.7rem", color: "text.secondary" }}>
                      ACCIONES
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows?.length ? (
                  rows.map((row, idx) => {
                    const rowId = getRowId(row, idx);
                    const isItemSelected = selected.includes(rowId);

                    const colorPlaca = placaColorMap.get(row?.placa);

                    return (
                      <TableRow
                        key={rowId}
                        hover
                        selected={isItemSelected}
                        sx={{
                          height: 32,
                          ...(colorPlaca && {
                            bgcolor: (t) => alpha(colorPlaca, t.palette.mode === "dark" ? 0.16 : 0.30),
                          }),
                          ...(getRowSx ? getRowSx(row) : {}),
                        }}
                      >
                        {checkboxEnabled && (
                          <TableCell sx={{ pl: 1 }}>
                            <Checkbox size="small" checked={isItemSelected}
                              onChange={() => handleClick(rowId)} sx={{ p: 0.5 }} />
                          </TableCell>
                        )}

                        {columns.map((col) => {
                          const clickable = isCellClickable?.(row, col);
                          const content = col.render ? col.render(row) : row?.[col.key] ?? "—";

                          return (
                            <TableCell
                              key={col.key}
                              onClick={clickable ? () => onCellClick?.(row, col) : undefined}
                              sx={{
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                                px: 0.5,
                                ...(clickable && {
                                  cursor: "pointer",
                                  color: "#006d77",
                                  fontWeight: 700,
                                  textDecoration: "underline",
                                  textUnderlineOffset: "2px",
                                }),
                                // ✅ Estilo especial para placa: color fijo por texto/link (el tinte
                                // de fondo por placa se aplica a toda la fila, ver TableRow arriba).
                                ...(col.key === "placa" && {
                                  fontWeight: 600,
                                  color: clickable ? "#006d77" : "#1976d2",
                                  ...(colorPlaca && { borderLeft: `3px solid ${colorPlaca}` }),
                                }),
                                ...col.cellSx,
                              }}
                            >
                              {content}
                            </TableCell>
                          );
                        })}

                        {editEnabled && (
                          <TableCell align="right" sx={{ pr: 1 }}>
                            <IconButton size="small" onClick={() => onEdit?.(row)} sx={{ p: 0.2 }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + (checkboxEnabled ? 1 : 0) + (editEnabled ? 1 : 0)} align="center"
                      sx={{ py: 2, fontSize: "0.75rem", color: "text.secondary" }}>
                      No hay registros para mostrar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && (
            <TablePagination
              component="div"
              count={total}
              page={pageZeroBased}
              rowsPerPage={limit}
              onPageChange={(_, newPage) => onPageChange?.(newPage)}
              onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
              rowsPerPageOptions={rowsPerPageOptions}
              labelRowsPerPage="Límite"
              sx={{
                "& .MuiTablePagination-toolbar": { minHeight: 40 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.75rem",
                },
              }}
            />
          )}
        </>
      )}
    </Paper>
  );
}
